import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import InterestsOutlinedIcon from '@mui/icons-material/InterestsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MicOutlinedIcon from '@mui/icons-material/MicOutlined';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { Box, Button, Divider, Stack, Typography, type SvgIconTypeMap } from '@mui/material';
import { type OverridableComponent } from '@mui/material/OverridableComponent';
import { Link, useLocation, useRouter } from '@tanstack/react-router';
import { useSnackbar } from 'notistack';

import { UserRole } from 'entities/user';
import { useLogoutMutation } from 'features/auth';
import { Routes } from 'shared/routes';

import { styles } from './styles';

type IconComponent = OverridableComponent<SvgIconTypeMap<object, 'svg'>> & {
  muiName: string;
};

interface INavigationItem {
  id: string;
  label: string;
  icon: IconComponent;
  disabled: boolean;
  path: string;
}

interface INavigationSection {
  title: string;
  items: INavigationItem[];
}

const clientNavigationSections: INavigationSection[] = [
  {
    title: 'Navigation',
    items: [
      {
        id: 'search',
        label: 'Search',
        icon: SearchIcon,
        disabled: true,
        path: Routes.frontend.rootPath,
      },
      {
        id: 'relationships',
        label: 'Relationships',
        disabled: true,
        icon: AccountTreeOutlinedIcon,
        path: Routes.frontend.rootPath,
      },
      {
        id: 'requests',
        label: 'Requests',
        disabled: true,
        icon: ArticleOutlinedIcon,
        path: Routes.frontend.rootPath,
      },
    ],
  },
];

const editorialNavigationItemsBase: INavigationItem[] = [
  {
    id: 'requests-list',
    label: 'Requests (list)',
    icon: ViewListOutlinedIcon,
    path: Routes.frontend.adminRequestsPath,
    disabled: false,
  },
  {
    id: 'requests-kanban',
    label: 'Requests (board)',
    icon: ViewKanbanOutlinedIcon,
    path: Routes.frontend.adminRequestsBoardPath,
    disabled: false,
  },
  {
    id: 'profiles',
    label: 'Profiles',
    icon: AccountBoxOutlinedIcon,
    path: Routes.frontend.adminProfilesPath,
    disabled: false,
  },
  {
    id: 'organizations',
    label: 'Organizations',
    icon: BusinessOutlinedIcon,
    path: Routes.frontend.adminOrganizationsPath,
    disabled: false,
  },
  {
    id: 'interests',
    label: 'Interests',
    icon: InterestsOutlinedIcon,
    path: Routes.frontend.adminInterestsPath,
    disabled: false,
  },
  {
    id: 'industries',
    label: 'Industries',
    icon: FactoryOutlinedIcon,
    path: Routes.frontend.adminIndustriesPath,
    disabled: false,
  },
  {
    id: 'business-functions',
    label: 'Business Functions',
    icon: WorkOutlineIcon,
    path: Routes.frontend.adminBusinessFunctionsPath,
    disabled: false,
  },
  {
    id: 'quartr-transcripts',
    label: 'Quartr Transcripts',
    icon: MicOutlinedIcon,
    path: Routes.frontend.adminQuartrEventTranscriptsPath,
    disabled: false,
  },
];

const editorialEvaluationsItem: INavigationItem = {
  id: 'evaluations',
  label: 'Evaluations',
  icon: AssessmentOutlinedIcon,
  path: Routes.frontend.adminEvaluations,
  disabled: false,
};

const getEditorialNavigationSection = (role: UserRole): INavigationSection => ({
  title: 'Editorial',
  items:
    role === UserRole.Admin
      ? [...editorialNavigationItemsBase, editorialEvaluationsItem]
      : editorialNavigationItemsBase,
});

const adminNavigationSection: INavigationSection = {
  title: 'Admin',
  items: [
    {
      id: 'dashboards',
      label: 'Dashboards',
      icon: SpaceDashboardOutlinedIcon,
      path: Routes.frontend.adminDashboardsPath,
      disabled: false,
    },
    {
      id: 'value-dashboard',
      label: 'Value',
      icon: InsightsOutlinedIcon,
      path: Routes.frontend.adminValueDashboardPath,
      disabled: false,
    },
    {
      id: 'accounts',
      label: 'Accounts',
      icon: AccountCircleOutlinedIcon,
      path: Routes.frontend.adminAccountsPath,
      disabled: false,
    },
    {
      id: 'users',
      label: 'Users',
      icon: PersonIcon,
      path: Routes.frontend.adminUsersPath,
      disabled: false,
    },
  ],
};

const isNavigationItemActive = (pathname: string, item: INavigationItem): boolean => {
  if (item.id === 'requests-list') {
    return pathname === Routes.frontend.adminRequestsPath;
  }
  if (item.id === 'requests-kanban') {
    return pathname === Routes.frontend.adminRequestsBoardPath;
  }
  if (item.id === 'dashboards') {
    return pathname === Routes.frontend.adminDashboardsPath;
  }
  if (item.disabled && item.path === Routes.frontend.rootPath) {
    return false;
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
};

const getNavigationSections = (role: UserRole, isAdminMode: boolean): INavigationSection[] => {
  if (!isAdminMode) {
    return clientNavigationSections;
  }

  switch (role) {
    case UserRole.Admin:
      return [getEditorialNavigationSection(role), adminNavigationSection];
    case UserRole.Editor:
      return [getEditorialNavigationSection(role)];
    default:
      return clientNavigationSections;
  }
};

interface ISidebarProps {
  isAdminMode?: boolean;
  activeItem?: string;
  userRole?: UserRole;
}

const Sidebar = ({ isAdminMode = false, activeItem, userRole = UserRole.Client }: ISidebarProps) => {
  const router = useRouter();
  const { pathname } = useLocation();
  const [logout] = useLogoutMutation();
  const { enqueueSnackbar } = useSnackbar();

  const navigationSections = getNavigationSections(userRole, isAdminMode);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      enqueueSnackbar('Logged out successfully', { variant: 'success' });
      router.navigate({ to: Routes.frontend.loginPath });
    } catch {
      enqueueSnackbar('Logout failed. Please try again.', { variant: 'error' });
    }
  };

  return (
    <Box sx={styles.sidebar}>
      <Box sx={styles.logoContainer}>
        <Box sx={styles.logoPlaceholder}>{/* Logo icon placeholder */}</Box>
        <Typography sx={styles.logoText}>ExecutiveIQ</Typography>
      </Box>

      <Divider />

      <Stack sx={styles.navigation}>
        {navigationSections.map((section, sectionIndex) => (
          <Box key={section.title}>
            <Typography sx={[styles.sectionHeader, sectionIndex === 0 && styles.sectionHeaderFirst]}>
              {section.title}
            </Typography>
            <Stack sx={styles.sectionItems}>
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeItem ? activeItem === item.id : isNavigationItemActive(pathname, item);

                return (
                  <Button
                    key={item.id}
                    component={Link}
                    to={item.path}
                    sx={[styles.navButton, isActive && styles.navButtonActive]}
                    startIcon={<Icon sx={styles.icon} />}
                    disabled={item.disabled}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>

      <Box sx={styles.footer}>
        <Button sx={styles.signOutButton} startIcon={<LogoutIcon sx={styles.icon} />} onClick={handleLogout} fullWidth>
          Sign Out
        </Button>
      </Box>
    </Box>
  );
};

export { Sidebar };
